import { Field, TextInput, SelectInput } from '../ui/Field.jsx'
import { OptionSet } from '../items/OptionSet.jsx'
import { optionsForField } from '../platformLists.js'
import { detailVisible, toTimeInputValue, toTextValue } from './validate.js'

/**
 * One demographics field, rendered from its declared `type`.
 *
 * Same principle as the item renderers: dispatch on the type config declares,
 * never on the field's name. A new field of a known type needs no code.
 *
 * Two option shapes meet here. config's demographics options are plain strings
 * where the stored value IS the label; country and timezone carry
 * options_source: "client" and no list, so the platform supplies {value, label}
 * pairs whose value is the ISO code / IANA id. optionsForField normalises both.
 */
/*
 * F9 — DEMOGRAPHICS NOW OBEYS DS-2 §2.2, THE RULE THAT WAS ALREADY WRITTEN.
 *
 * This screen rendered every choice as full-width stacked cards: sex 310px,
 * education 470px, employment 560px, 3,262px of scroll in total. §2.2 has said
 * since it was written that `options <= 7 AND worst label <= 28` is ROW layout,
 * and every one of these qualifies — the worst labels are 17, 12 and 18
 * characters. The rule was there; this screen simply never applied it.
 *
 * It also rendered THREE different controls for the same kind of question —
 * cards, a native select, another select — adjacent on one screen. §3.2 is
 * explicit that categorical is an OptionSet and never a <select>.
 *
 * So it now delegates to OptionSet, the same component the battery uses, and
 * inherits §2.2's rule rather than restating it. That is the point: a second
 * copy of the row/card threshold here is how the two would drift. It also means
 * F8's reflow fix applies to this screen for free.
 *
 * THE ONE EXCEPTION, and it is a limit of the rule rather than a disagreement
 * with it: country is 251 options and timezone is several hundred. §2.2's rule
 * sends anything over 7 to CARD layout, and 251 cards is not a design. Long
 * lists keep the native select, which is also the control a phone gives a
 * searchable wheel for. The rule was written for scales, not for atlases.
 */
const OPTION_SET_MAX = 8

export function DemographicField({ field, value, values, onChange, errors }) {
  const error = errors?.[field.field_id]
  const showDetail = detailVisible(field, value)

  return (
    <>
      <Control field={field} value={value} onChange={(v) => onChange(field.field_id, v)} error={error} />
      {showDetail ? (
        <div className="-mt-2 mb-4 border-l-2 border-[var(--line)] pl-4">
          <Control
            field={field.detail}
            value={values?.[field.detail.field_id]}
            onChange={(v) => onChange(field.detail.field_id, v)}
            error={errors?.[field.detail.field_id]}
          />
        </div>
      ) : null}
    </>
  )
}

function Control({ field, value, onChange, error }) {
  const id = field.field_id
  const options = optionsForField(field)
  const label = field.required ? field.label : `${field.label} (optional)`

  if (field.type === 'integer') {
    /*
     * DS-2 §3.3 — 8em WIDE, NOT FULL WIDTH, and the range stated beneath.
     *
     * This was a 358px field for a two-digit answer. A number box that spans
     * the screen invites a sentence, and it left the stepper arrows stranded
     * against the far edge, twelve pixels of chrome a thumb will never
     * deliberately hit. The steppers themselves are gone in index.css, since
     * "no steppers" is a rule about every numeric field and not about this one.
     *
     * The range comes from config's own min/max rather than placeholder text,
     * so it stays visible while she types.
     */
    return (
      <Field id={id} label={label} hint={field.hint ?? null} error={error}>
        <TextInput
          id={id}
          error={error}
          hint={field.hint ?? null}
          data-stepper={field.readonly ? undefined : ''}
          type="number"
          inputMode="numeric"
          pattern="[0-9]*"
          min={field.min}
          max={field.max}
          value={value ?? ''}
          /*
           * readonly (config): the value is DERIVED - age from the dob - so
           * the control displays it greyed and takes no input. disabled, not
           * readOnly, deliberately: readOnly still focuses and shows a caret,
           * which invites the edit it will not accept. No steppers either -
           * arrows on a number she cannot change are a lie in chrome form.
           */
          disabled={field.readonly === true}
          onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
          /*
           * Inline, not a utility class. `w-full` is already on the base and
           * two width utilities of equal specificity are resolved by stylesheet
           * ORDER, not by the order they appear in the attribute — measured at
           * 358px with `w-[8em]` applied and losing. An inline style always
           * wins, which is the one thing that makes this deterministic.
           */
          style={{ width: '8em', maxWidth: '100%' }}
        />
      </Field>
    )
  }

  if (field.type === 'time') {
    return (
      <Field
        id={id}
        label={label}
        // The wrapping range is stated plainly rather than left to be discovered
        // by a rejection: bed time is allowed to be after midnight.
        /*
         * 24-HOUR, ALWAYS — OPERATOR RULING, REVERSING MY F9 FIX.
         *
         * F9 found "Between 04:00 and 11:00" four pixels above a field
         * rendering "07:00 AM", and I resolved it by formatting the hint in the
         * DEVICE's convention so the two agreed. The reasoning was that DS-2
         * §3.4's "24-hour everywhere" is only half reachable: `input[type=time]`
         * renders in the BROWSER's own UI language, not the page's — verified at
         * 390x844, `lang="en-GB"` on the input changes nothing and neither does
         * the document locale — so I matched the half I could not control.
         *
         * OVERRULED, and the spec is right. Matching the device made the app's
         * own text vary by phone, so what "04:00" meant depended on a setting we
         * never see, in the field that generates the entire beep schedule. A
         * 24-hour string is unambiguous on every device, it is the format the
         * value is STORED in, and it is what every other time in this app says.
         *
         * What remains is real and is not mine to close: on a 12-hour phone the
         * native picker still shows "07:00 AM" under a 24-hour hint. That is the
         * platform's rendering of a control the page cannot format. Closing it
         * means not using `input[type=time]`, which is a design decision rather
         * than a defect fix.
         */
        hint={field.hint ?? timeRangeHint(field.min, field.max)}
        error={error}
      >
        <TextInput
          id={id}
          error={error}
          hint
          type="time"
          step={60}
          // normalised, because the API returns HH:MM:SS and the control only
          // renders HH:MM — an unnormalised value silently shows as blank
          value={toTimeInputValue(value)}
          onChange={(e) => onChange(e.target.value)}
        />
      </Field>
    )
  }

  if (field.type === 'free_text') {
    return (
      <Field id={id} label={label} hint={field.hint} error={error}>
        <TextInput
          id={id}
          error={error}
          hint={field.hint}
          type="text"
          // the API can return a free_text field as a single-element array
          value={toTextValue(value)}
          onChange={(e) => onChange(e.target.value)}
        />
      </Field>
    )
  }

  if (field.type === 'multi_select') {
    const selected = Array.isArray(value) ? value : []
    return (
      <Field id={id} label={label} hint={field.hint} error={error}>
        <div className="flex flex-col gap-2">
          {options.map((option) => (
            <label
              key={option.value}
              className={
                'flex cursor-pointer items-center gap-3 rounded-[var(--r-surface)] border px-4 py-3 ' +
                (selected.includes(option.value)
                  ? 'border-[var(--accent)] bg-[var(--accent-tint)]'
                  : 'border-[var(--control-border)] bg-[var(--surface-raised)]')
              }
            >
              <input
                type="checkbox"
                checked={selected.includes(option.value)}
                onChange={(e) =>
                  onChange(
                    e.target.checked
                      ? [...selected, option.value]
                      : selected.filter((v) => v !== option.value),
                  )
                }
                className="h-5 w-5 shrink-0 accent-[var(--accent)]"
              />
              <span className="text-base leading-snug text-[var(--ink-900)]">{option.label}</span>
            </label>
          ))}
        </div>
      </Field>
    )
  }

  if (field.type === 'select_or_text') {
    // CONFIG'S OPTIONS AND NOTHING ELSE.
    //
    // This appended a synthetic "Something else" entry, so demo_gender offered
    // both config's own "Other" and an invented option meaning the same thing:
    // two ways to give one answer, only one of them from config, and the
    // invented one storing a value config never defined.
    //
    // config does not declare which option should reveal a free-text box, and
    // hardcoding the string "Other" here would be the same mistake wearing a
    // different hat. So this renders as a plain single select until config says
    // otherwise. A stored value that is not in the list still round-trips into
    // a text box rather than being silently dropped.
    const known = options.some((o) => o.value === value)
    const customValue = !isEmpty(value) && !known

    return (
      <Field id={id} label={label} hint={field.hint} error={error}>
        <SelectInput
          id={id}
          error={error}
          hint={field.hint}
          value={customValue ? '' : (value ?? '')}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">Choose one</option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </SelectInput>
        {customValue ? (
          <input
            type="text"
            aria-label={`${field.label}, please describe`}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="mt-2 w-full rounded-[var(--r-surface)] border border-[var(--control-border)] bg-[var(--surface-raised)] px-4 py-3 text-base"
          />
        ) : null}
      </Field>
    )
  }

  // single_select, country, timezone — one choice from a list.
  const useOptionSet = options.length > 0 && options.length <= OPTION_SET_MAX

  return (
    <Field id={id} label={label} hint={field.hint} error={error}>
      {useOptionSet ? (
        /*
         * The battery's control, not a copy of it. layout is left undefined so
         * preferredLayout() applies §2.2's threshold — row for these, card if a
         * field ever arrives with long labels. `format` is a shim because
         * OptionSet reads `format.options` and nothing else; demographics
         * options are already {value,label} pairs.
         */
        <OptionSet
          format={{ options }}
          /*
           * Wider than a scale's, because these labels are single long words
           * ("Postgraduate", "Unemployed") rather than short anchors. Sized to
           * the longest WORD so no cell is ever narrower than the word in it,
           * which is what stops the mid-word chopping. Capped at 8ch so one
           * outlier cannot force a single column and undo the whole fix.
           */
          name={id}
          answer={{ value }}
          onChange={(next) => onChange(next.value)}
          ariaLabelledBy={`${id}-label`}
        />
      ) : (
        <SelectInput
          id={id}
          error={error}
          hint={field.hint}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">Choose one</option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </SelectInput>
      )}
      {options.length === 0 ? (
        <p className="mt-1 text-sm text-[var(--ink-700)]">
          This list hasn&rsquo;t loaded. Please try again shortly.
        </p>
      ) : null}
    </Field>
  )
}

const isEmpty = (v) => v === null || v === undefined || v === ''

/**
 * A wake/bed range, always on a 24-hour clock (DS-2 §3.4).
 *
 * The locale is PINNED rather than left to the runtime, which is the whole
 * point: `undefined` means "follow the device", and that made the app's own
 * sentence say different things on different phones. Returns undefined when
 * config states no range, and falls back to the raw values if Intl refuses the
 * input rather than showing a mangled time — a wrong time here reschedules
 * every beep she gets.
 */
export function timeRangeHint(min, max) {
  if (!min || !max) return undefined
  const shown = [min, max].map((t) => formatClock(t))
  return `Between ${shown[0]} and ${shown[1]}.`
}

function formatClock(hhmm) {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(hhmm))
  if (!m) return hhmm
  try {
    const d = new Date(2000, 0, 1, Number(m[1]), Number(m[2]))
    return new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false }).format(d)
  } catch {
    return hhmm
  }
}
